import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  MutableRefObject,
  ReactNode,
  isValidElement,
  Dispatch,
  SetStateAction,
  ReactElement,
  useContext,
} from 'react';
import clsx from 'clsx';
import {
  useQuery,
} from '@tanstack/react-query';
import {
  ColumnDef,
  getCoreRowModel as getBaseCoreRowModel,
  getSortedRowModel as getBaseSortedRowModel,
  getExpandedRowModel as getBaseExpandedRowModel,
  Row,
  SortingState,
  ExpandedState,
  PaginationState as ReactTablePaginationState,
  useReactTable,
  Table,
} from '@tanstack/react-table';
import empty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import sumBy from 'lodash/sumBy';
import isFunction from 'lodash/isFunction';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import {
  Pagination as PaginationState,
} from '@/types/common';
import {
  environment,
} from '@/env';
import {
  ColumnDefMeta,
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import VirtualList from 'core/components/VirtualList';
import TableHeader from 'core/components/VirtualTable/Header';
import TableFooter from 'core/components/VirtualTable/Footer';
import TableRow from 'core/components/VirtualTable/Row';
import Pagination from 'core/components/VirtualTable/Pagination';
import withEditableCell from 'core/components/VirtualTable/withEditableCell';
import times from 'lodash/times';
import {
  DataContext,
  ContextProps as DataContextProps,
} from '@/core/context/data';
import Spinner from '@/components/Spinner';

export type TableParts = 'root'
  // header related
  | 'header'
  | 'outerColumn'
  | 'column'
  | 'outerHeader'
  // body related
  | 'body'
  | 'scrollContainer'
  | 'grid'
  | 'outerRow'
  | 'row'
  | 'cell'
  // sub row related
  | 'subRow'
  // footer related
  | 'footer'
  | 'pagination';

type TableSkeletonProps = {
  rows: number;
};

type Props<T> = {
  /// @TODO: merge data into getData
  // so, loading/empty/error state will be managed
  // by table component and not from outside
  /// @deprecated: use DataProvider
  data?: T[];
  /// @deprecated: use DataProvider instead
  getData?: (...props: any[]) => Promise<T[]>;
  updateData?: (index: number, id: string, value: any, original: T) => void;
  removeData?: (index: number, original: T) => void;
  /// @deprecated: use DataProvider
  queryParams?: Record<string, any>;
  columns: ColumnDef<T>[];
  defaultSorting?: SortingState;
  /// @deprecated: use DataProvider
  defaultPagination?: PaginationState;
  defaultColumn?: Partial<ColumnDef<T, unknown>>;
  className?: string;
  onRowClick?: (table: Table<T>, row: Row<T>) => void;
  getRowId?: (row: T, index: number) => any;
  dataKey?: string;
  itemSize?: number;
  hasFooter?: boolean;
  classes?: Partial<Record<TableParts, string | ((...params: any[]) => string)>>;
  instanceRef?: MutableRefObject<VirtualTableInstance>;
  loadingSlot?: string | ReactNode;
  loading?: boolean;
  emptySlot?: string | ReactNode;
  renderRowComponent?: (data: Row<T>) => ReactElement;
  disabled?: boolean;
  overscan?: number;
  skeleton?: TableSkeletonProps;
  getSubRows?: ((row: T, index: number) => T[] | undefined);
  getRowCanExpand?: ((row: Row<T>) => boolean);
  expanded?: ExpandedState;
  onEndReached?: () => void;
  endReachedOffset?: number;
  onScrollElementRef?: (el: HTMLDivElement | null) => void;
  endTriggerOnceUntilDataChange?: boolean;
  tableMeta?: Record<string, any>;
};

function getLegacyData<T>({
  data = [],
  disabled,
  dataKey,
  queryParams,
  getData,
}: Pick<Props<T>, 'data' | 'disabled' | 'dataKey' | 'queryParams' | 'getData'>) {
  return !isNil(getData)
    ? useQuery<T[]>({
      queryKey: [dataKey ?? '', ...values(queryParams)],
      queryFn: () => getData(queryParams),
      enabled: !disabled,
    })
    : { data };
}

function useLegacyPagination(defaultPagination: Partial<PaginationState> = {}) {
  const includePagination = !isNil(defaultPagination);
  const [totalSize, setTotalSize] = includePagination
    ? useState<number>(0)
    : [0];
  const [pagination, setPagination] = includePagination
    ? useState<PaginationState>({
      pageSize: defaultPagination?.pageSize ?? 25,
      pageIndex: defaultPagination?.pageIndex ?? 0,
      page: defaultPagination?.page ?? 0,
    }) : [];

  return {
    totalSize: totalSize ?? 0,
    setTotalSize: (setTotalSize ?? noop),
    pagination: (pagination ?? { page: 0, pageSize: 25, pageIndex: 0 }),
    setPagination: (setPagination ?? noop),
  };
}

export default function VirtualTable<T>({
  instanceRef,
  defaultSorting,
  expanded: defaultExpanded = {},
  getData,
  queryParams = {},
  data: _data,
  columns,
  className = '',
  onRowClick,
  getRowId,
  defaultPagination,
  defaultColumn,
  dataKey,
  itemSize,
  updateData,
  removeData,
  hasFooter = false,
  classes,
  loadingSlot,
  loading: _loading,
  emptySlot: _emptySlot,
  renderRowComponent: _renderRowComponent,
  disabled,
  overscan,
  skeleton = { rows: 3 },
  getSubRows,
  getRowCanExpand,
  onEndReached,
  endReachedOffset,
  onScrollElementRef,
  endTriggerOnceUntilDataChange,
  tableMeta,
}: Props<T>) {
  /// table features
  const includeSorting = !isNil(defaultSorting);
  const hasSkeleton = !isNil(skeleton);
  const dataProvider = useContext(DataContext);
  /// @TODO: throw exception once data provider
  /// will be implemented everywhere
  const hasDataProvider = !empty(dataProvider);
  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded);
  const [sorting, setSorting] = includeSorting
    ? useState<SortingState>(defaultSorting)
    : [];
  const paginationProps = hasDataProvider
    ? dataProvider
    : useLegacyPagination(defaultPagination);
  const {
    setTotalSize,
    totalSize = 0,
    pagination,
    setPagination,
  } = (paginationProps ?? {});

  const includePagination = !empty(pagination);

  const onPaginationChange = (
    change: (value: ReactTablePaginationState) => ReactTablePaginationState,
  ) => {
    const { pageIndex = 0, pageSize = 0 } = change(pagination as ReactTablePaginationState);
    setPagination?.({
      page: pageIndex,
      pageIndex,
      pageSize,
    });
  };

  const params = {
    sorting,
    pagination,
    ...queryParams,
    setTotalSize,
  };

  const {
    data = _data,
    refetch = null,
    isLoading = false,
    isFetching = false,
    isEmpty = false,
    isError = false,
  } = hasDataProvider
    ? dataProvider
    /// @deprecated: use data provider
    : getLegacyData({
      data: _data,
      dataKey,
      disabled,
      getData,
      queryParams: params,
    }) as DataContextProps;

  const loading = !isNil(_loading) ? _loading : isLoading;

  const tableData = useMemo(
    () => (hasSkeleton && loading && (data ?? []).length === 0
      ? times(skeleton?.rows ?? 3, () => ({} as T)) ?? []
      : data),
    [skeleton, data, loading],
  );

  const table = useReactTable({
    data: tableData ?? [],
    columns: columns.map((column) => (
      (column.meta as ColumnDefMeta<T>)?.editable
        ? withEditableCell<T>(column)
        : column
    )),
    getRowId,
    state: {
      expanded,
      ...(includeSorting ? { sorting } : {}),
      ...(includePagination ? { pagination } : {}),
    },
    ...(includePagination ? {
      manualPagination: true,
      onPaginationChange: onPaginationChange as Dispatch<SetStateAction<ReactTablePaginationState>>,
    } : {}),
    ...(includeSorting ? {
      onSortingChange: setSorting,
      getSortedRowModel: getBaseSortedRowModel(),
    } : {}),
    onExpandedChange: setExpanded,
    paginateExpandedRows: false,
    getExpandedRowModel: getBaseExpandedRowModel(),
    getRowCanExpand,
    getSubRows,
    enableSorting: includeSorting,
    getCoreRowModel: getBaseCoreRowModel(),
    debugTable: environment === 'local',
    meta: {
      updateData: updateData ?? noop,
      removeData: removeData ?? noop,
      ...(tableMeta ?? {}),
    },
    ...(isNil(defaultColumn) ? {} : {
      defaultColumn,
    }),
  });

  const {
    getSortedRowModel,
    getHeaderGroups,
    getFooterGroups,
    getState,
    setPageIndex,
    setPageSize,
  } = table;

  const tableContainerMinWidth = sumBy(
    columns,
    ({ size = 100, minSize = 100 }) => minSize ?? size,
  );

  const rootClassName = clsx(
    'populate-table-container w-full h-full flex flex-col overflow-hidden',
    className,
    isFunction(classes?.root) ? classes!.root() : classes?.root,
    { 'pb-2': includePagination },
  );

  const { rows } = getSortedRowModel();

  // sorted row model doesn't provide a proper row index(sorted)
  // it's carrying over row index as it is in data
  // in this forEach we are manually assigning a proper sorted data index
  rows.forEach((row, index) => {
    row.index = index;
  });

  const onClick = useCallback(
    (row: Row<T>) => { onRowClick?.call(null, table, row); },
    [table, onRowClick],
  );

  const rowClasses = clsx(
    'border border-transparent border-b-[#dde2ee] first:border-t first:border-t-[#dde2ee]',
    'group hover:rounded hover:border-gray-400',
  );

  useEffect(() => { refetch?.(); }, [pagination, sorting]);

  const hasData = (tableData ?? []).length > 0;

  const message = useMemo(() => {
    if (isError) {
      return 'Oops, something went wrong. Try to refresh the page.';
    }
    if (isEmpty) {
      return 'No data available.';
    }
    return null;
  }, [isError, isEmpty]);

  useEffect(() => {
    if (!isNil(instanceRef)) {
      instanceRef.current = {
        refetch,
        sorting,
        pagination,
        setPageIndex,
        isLoading,
        isFetching,
      };
    }
  }, [refetch, sorting, pagination, setPagination, isLoading, isFetching]);

  const loader = !isValidElement(loadingSlot)
    ? (
      <div className="flex justify-center items-center gap-2">
        <Spinner className="!font-bold" size={30} />
        {typeof loadingSlot === 'string' && loadingSlot}
      </div>
    )
    : loadingSlot;

  const emptySlot = typeof _emptySlot === 'string'
    ? (
      <div className="flex flex-1 justify-center items-center">
        {_emptySlot}
      </div>
    )
    : _emptySlot;

  return (
    <div className={rootClassName}>
      <div
        className={clsx(
          'populate-table w-full flex flex-1 flex-col overflow-hidden',
          { 'items-center justify-center': loading || isError },
        )}
      >
        {(loading && !hasSkeleton) && loader}
        <div className="flex h-full w-full overflow-x-auto">
          <div
            className="flex w-full h-full flex-col"
            style={{ minWidth: tableContainerMinWidth }}
          >
            <TableHeader<T>
              headerGroups={getHeaderGroups()}
              classes={pick(classes, ['header', 'outerColumn', 'column'])}
              className={isFunction(classes?.outerHeader)
                ? classes!.outerHeader()
                : classes?.outerHeader}
            />
            {hasData ? (
              <VirtualList<Row<T>>
                renderItemComponent={isFunction(_renderRowComponent)
                  ? _renderRowComponent
                  : (row: Row<T>) => (
                    <TableRow<T>
                      row={row}
                      onClick={onClick}
                      className={isFunction(classes?.row) ? classes!.row(row) : classes?.row}
                      cellClassName={classes?.cell}
                      subRowClassName={classes?.subRow}
                      hasSkeleton={hasSkeleton && loading}
                    />
                  )}
                data={rows}
                className={clsx(
                  'populate-table-body w-full',
                  isFunction(classes?.body)
                    ? classes!.body
                    : classes?.body,
                )}
                scrollContainerClassName={clsx(
                  'pb-4',
                  isFunction(classes?.scrollContainer)
                    ? classes!.scrollContainer()
                    : classes?.scrollContainer,
                )}
                gridClassName={isFunction(classes?.grid)
                  ? classes!.grid()
                  : classes?.grid}
                rowClassName={(item) => clsx(
                  rowClasses,
                  isFunction(classes?.outerRow)
                    ? classes!.outerRow(item)
                    : classes?.outerRow,
                )}
                itemSize={itemSize}
                overscan={overscan}
                onEndReached={onEndReached}
                endReachedOffset={endReachedOffset}
                onScrollElementRef={onScrollElementRef}
                endTriggerOnceUntilDataChange={endTriggerOnceUntilDataChange}
              />
            ) : (
              <>
                {!isNil(emptySlot) && emptySlot}
                {isNil(emptySlot) && message && (
                  <div className="p-4 text-sm text-slate-500">{message}</div>
                )}
              </>
            )}
            {hasFooter && hasData && (
              <TableFooter<T>
                footerGroups={getFooterGroups()}
                className={isFunction(classes?.footer)
                  ? classes!.footer()
                  : classes?.footer}
              />
            )}
          </div>
        </div>
      </div>
      {includePagination && totalSize > 0 && !isFetching && hasData && (
        <Pagination
          {...getState().pagination}
          className={isFunction(classes?.pagination) ? classes!.pagination() : classes?.pagination}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          totalItems={totalSize}
        />
      )}
    </div>
  );
}
